import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_116: DocMigration = {
  maxSchemaVersion: "0.0.116",
  description: "Exterior and Interior Props to Catalog Structure",
  apply(json: any) {
    for (const settingsId in json.settings) {
      const setting = json.settings[settingsId];
      if (setting.type === "EXTERIOR_PROP_SETTINGS" || setting.type === "INTERIOR_PROP_SETTINGS") {
        const propTypes = {};
        for (const key in setting) {
          if (["id", "name", "type", "parentId", "sortIndex", "entityType"].includes(key)) {
            continue;
          }
          //make catalog entry
          const propId = setting[key];
          propTypes[key] = makeCatalogEntry(key, propId);
          delete setting[key];
        }
        setting["propTypes"] = propTypes;
      }
    }

    return json;
  },
};

export default migration_0_0_116;

function makeCatalogEntry(key: string, propId: string) {
  const entry = {};

  //basic propAttributes
  entry["id"] = key;
  entry["name"] = key;
  entry["propId"] = propId;

  //add constants to propAttributes
  //floorPlanPrettyName
  if (propPrettyFloorPlanName[key]) {
    entry["floorPlanPrettyName"] = propPrettyFloorPlanName[key];
  }
  //defaultVerticalOffset
  if (propHeightOffset[key]) {
    entry["defaultVerticalOffset"] = propHeightOffset[key];
  }
  //planOffset
  if (propPlanOffset[key]) {
    entry["planOffset"] = propPlanOffset[key];
  }
  //isExteriorArchitecturalProp
  if (isExteriorArchitecturalProp[key]) {
    entry["isExteriorArchitecturalProp"] = true;
  }
  //isInteriorArchitecturalProp
  if (isInteriorArchitecturalProp[key]) {
    entry["isInteriorArchitecturalProp"] = true;
  }
  //excludeFromExteriorViews
  if (isExteriorFurnitureProp[key]) {
    entry["excludeFromExteriorViews"] = true;
  }
  //forceOnExteriorViews
  if (forceExteriorProp[key]) {
    entry["forceOnExteriorViews"] = true;
  }
  //isPlumbingArchitecturalProp
  if (isPlumbingArchitecturalProp[key]) {
    entry["isPlumbingArchitecturalProp"] = true;
  }
  //plumbingTag
  if (plumbingPropTag[key]) {
    entry["plumbingTag"] = plumbingPropTag[key];
  }
  //drawOnlyFoundation
  if (isDrawOnlyProp[key]) {
    entry["drawOnlyFoundation"] = true;
  }
  //isUtilityArchitecturalProp
  if (isPlumbingUtilityArchitecturalProp[key]) {
    entry["isUtilityArchitecturalProp"] = true;
  }
  //isElectricalArchitecturalProp
  if (isElectricalFloorArchitecturalProp[key]) {
    entry["isElectricalArchitecturalProp"] = true;
  }
  //drawOnlyElectical
  if (isHardwareProp[key]) {
    entry["drawOnlyElectical"] = true;
  }
  //takeoffDescription
  if (interiorPropName[key]) {
    entry["takeoffDescription"] = interiorPropName[key];
  }
  //propBoundOrientation
  if (isLongProp[key]) {
    entry["propBoundOrientation"] = "LONG_PROP";
  }
  if (isCenterProp[key]) {
    entry["propBoundOrientation"] = "CENTER_PROP";
  }
  if (isCentroidProp[key]) {
    entry["propBoundOrientation"] = "CENTROID_PROP";
  }

  return entry;
}

//Below are constants that were removed and incorporated into propAttributes
const isExteriorArchitecturalProp: { [key: string]: boolean } = {
  craftColumn: true,
  craftOrnament: true,
  rakeBracket: true,
  awningBracket: true,
  bracketFrenchCountry: true,
  bracketTallTapered: true,
  exteriorLamp: true,
  beazerOrnament: true,
  angleBracket14x18: true,
  angleBracket20x20: true,
  angleBracket25x27: true,
  exteriorLampGas: true,
  shutters3050: true,
  soffitCorbel8x12: true,
  zenColumn: true,
  zenColumnAngled: true,
  elliottGableArch8: true,
  elliottGableArch9: true,
  elliottGableArch7: true,
  elliottGableArch12: true,
};
const isInteriorArchitecturalProp: { [key: string]: boolean } = {
  craftShutter: true,
  craftColumn: true,
};

const isExteriorFurnitureProp: { [key: string]: boolean } = {
  deckBench: true,
  deckSideTable: true,
  deckChair: true,
  deckChairSingle: true,
  deckCoffeeTable: true,
  deckDiningTable4Seat: true,
  deckDiningTable6Seat: true,
  deckSet: true,
  chairAdirondack: true,
};

const forceExteriorProp: { [key: string]: boolean } = {
  craftColumn: true,
  rakeBracket: true,
  awningBracket: true,
  exteriorLamp: true,
  angleBracket14x18: true,
  angleBracket20x20: true,
  angleBracket25x27: true,
  exteriorLampGas: true,
  shutters3050: true,
  soffitCorbel8x12: true,
  elliottGableArch8: true,
  elliottGableArch9: true,
  elliottGableArch7: true,
  elliottGableArch12: true,
};

const propPrettyFloorPlanName: { [key: string]: string } = {
  dishwasher: "DW",
  acUnit: "AC",
  ovenGas: "GAS",
  rangetopGas: "GAS",
  ovenWallGas: "GAS \n OVEN",
  ovenWallDoubleGas: "GAS \n DBL OVEN",
  exteriorLampGas: "GAS",
  dryer: "D",
  refrigerator: "REF",
  refrigeratorTop: "REF",
  tv: "TV",
  washer: "W",
  microwave: "MIC",
  bathtub: '36"X60"',
  waterHeater: "WH",
  waterHeaterGas: "GAS \n WH",
  tanklessWaterHeaterGas: "GAS \n TWH",
  tpHolder: "PH",
  towelHand: "TR",
  towelHook: "TH",
  towelBar: "TB",
  simplifire38: "S38",
  simplifire58: "S58",
  hoseBib: "HB",
  grillInsert: "GAS",
};

const propHeightOffset: { [key: string]: number } = {
  towelBar: 36,
  towelHand: 40,
  towelHook: 56,
  sink: 36,
  sinkDouble36: 36,
  sinkDouble: 36,
  sinkSingle: 36,
  sinkSingleApron: 36,
  sinkVanity: 36,
  hoseBib: 24,
};

const propPlanOffset: { [key: string]: number } = {
  hoseBib: -9,
};

const interiorPropName: { [key: string]: string } = {
  refrigerator: "refrigerator",
  oven: "oven",
  ovenWallDouble: "oven wall double",
  sinkPedestal: "sink pedestal",
  sinkVanity: "sink vanity",
  sinkDouble: "sink double",
  sinkDouble36: "sink double",
  sinkSingle: "sink single",
  sinkUtility: "sink utility",
  sinkSingleApron: "sink single apron",
  dishwasher: "dishwasher",
  rangetop: "rangetop",
  toilet: "toilet",
  toiletWashlet: "toilet washlet",
  dryer: "dryer",
  washer: "washer",
  bathtub: "bathtub",
  bathtubClaw: "bathtub claw",
  bathCombo: "bath combo",
  shower: "shower",
  microwave: "microwave",
  fireplace: "fireplace",
  fireplaceInsert: "fireplace insert",
  bookshelf5: "bookshelf 5",
  bookshelf6: "bookshelf 6",
};

const plumbingPropTag: { [key: string]: string } = {
  ovenGas: "GAS",
  rangetopGas: "GAS",
  ovenWallGas: "GAS",
  ovenWallDoubleGas: "GAS",
  exteriorLampGas: "GAS",
  grillInsert: "GAS",
  simplifire38: "GAS",
  simplifire58: "GAS",
  washer: "VTR",
  toilet: "VTR",
};

const isPlumbingArchitecturalProp: { [key: string]: boolean } = {
  // kitchen
  refrigerator: true,
  refrigeratorBottom: true,
  refrigeratorTop: true,
  refrigeratorSide: true,
  fridgeWine24: true,
  sinkPedestal: true,
  sinkVanity: true,
  sinkDouble: true,
  sinkDouble36: true,
  sinkSingle: true,
  sinkUtility: true,
  sinkSingleApron: true,
  dishwasher: true,
  fridgeUnderCounter: true,

  //  bathroom
  toilet: true,
  toiletWashlet: true,
  dryer: true,
  washer: true,
  bathtub: true,
  bathtub6042: true,
  bathtub7242: true,
  bathtub7245: true,
  bathtubBasin6042: true,
  bathtubClaw: true,
  bathCombo: true,
  shower: true,
  faucet: true,
  showerValve: true,
  showerPan36x36: true,
  showerPan42x42: true,
  showerPan60x36: true,
  showerPan60x42: true,

  // exterior
  outdoorFridge: true,
  sink: true,
};

const isPlumbingUtilityArchitecturalProp: { [key: string]: boolean } = {
  hoseBib: true,
};

const isElectricalFloorArchitecturalProp: { [key: string]: boolean } = {
  oven: true,
  ovenGas: true,
  ovenWallDouble: true,
  ovenWallDoubleGas: true,
  ovenWall: true,
  ovenWallGas: true,

  // exterior
  exteriorLampGas: true,
  grillGas: true,
  grillInsert: true,
};

const isHardwareProp: { [key: string]: boolean } = {
  tpHolder: true,
  towelHand: true,
  towelHook: true,
  towelBar: true,
};

const isDrawOnlyProp: { [key: string]: boolean } = {
  dishwasher: true,
  waterHeater: true,
};

const isLongProp: { [key: string]: boolean } = {
  bathtub6042: true,
  bathtub7242: true,
  bathtub7245: true,
  bathtubBasin6042: true,
  bathCombo: true,
};

const isCenterProp: { [key: string]: boolean } = {
  shower: true,
  showerValve: true,
};

const isCentroidProp: { [key: string]: boolean } = {
  showerPan36x36: true,
  showerPan42x42: true,
  showerPan60x36: true,
  showerPan60x42: true,
};
